import { uploadProjectDocuments, UploadProjectDocumentsData } from '@helios/frontend-api-v2-client';
import mime from 'mime';

import request from '../request';
import {
  ApiDocument,
  ApiOnboardingTask,
  ApiOnboardingTaskName,
  DocumentFile,
  UpdateNoUtilityBillParams,
} from './types';

import { ResourceContext } from '../../core/resourceContext';
import { getDocumentName } from './useUploadDocument';

const ENDPOINT = '/onboarding';

export const getDocumentUrls = async (
  name?: ApiOnboardingTaskName,
  documents: ApiDocument[] = [],
): Promise<DocumentFile[]> => {
  return Promise.all(
    documents.map(async (document, i) => {
      const url = `data:${document.contentType};base64,${document.data}`;
      const data = await fetch(url);
      const blob = await data.blob();
      const { type } = blob;
      const extension = mime.getExtension(type);
      const fileName = `${getDocumentName(name)}-${i + 1}.${extension}`;
      const file = new File([blob], name || 'unknown', { type: type || undefined });
      return {
        type: document.contentType,
        name: fileName,
        url: URL.createObjectURL(file),
      };
    }),
  );
};

export async function getTask(name?: ApiOnboardingTaskName, context?: ResourceContext): Promise<ApiOnboardingTask> {
  const { data: task } = await request({
    url: `${ENDPOINT}/${name}`,
    params: context ? { context: JSON.stringify(context) } : undefined,
  });
  const docUrls = await getDocumentUrls(name, task?.data?.documents);
  const utilityBillUrls = await getDocumentUrls(name, task?.data?.utilityBills);
  return { ...task, data: { ...task.data, documentUrls: [...docUrls, ...utilityBillUrls] } };
}

type ProjectDocumentFeedItemType = UploadProjectDocumentsData['body']['feedItemType'];

const TASK_NAME_TO_FEED_ITEM_TYPE: Partial<Record<ApiOnboardingTaskName, ProjectDocumentFeedItemType>> = {
  [ApiOnboardingTaskName.voidedCheck]: 'achRequirement',
  [ApiOnboardingTaskName.titleVerification]: 'titleVerification',
  [ApiOnboardingTaskName.utilityBill]: 'utilityBillRequirement',
  [ApiOnboardingTaskName.utilityBillRequirement]: 'utilityBillRequirement',
};

export async function uploadTaskDocument(
  name: ApiOnboardingTaskName,
  fileList: FileList,
  _applicationId?: string,
  context?: ResourceContext,
): Promise<void> {
  const feedItemType = TASK_NAME_TO_FEED_ITEM_TYPE[name];
  if (!feedItemType) {
    throw new Error(`Document upload is not supported for task ${name}`);
  }
  if (context?.type !== 'project' || !context.id) {
    throw new Error(`Cannot upload documents for task ${name} without a project context`);
  }

  await uploadProjectDocuments({
    path: { id: context.id },
    body: { feedItemType, file: Array.from(fileList) },
    throwOnError: true,
  });
}

export async function noUtilityBill(params: UpdateNoUtilityBillParams): Promise<void> {
  const { reason, description } = params;
  const data = {
    reason,
    description,
  };
  await request({ method: 'post', url: `${ENDPOINT}/noUtilityBill`, data });
}

